import { Button } from '@iterable/deprecated_react_components';
import DragAndDropList from 'core/dragAndDropList';
import {
  EMPTY_CRITERIA_PROPERTY,
  EMPTY_SEARCH_GROUP,
  EMPTY_SEARCH_QUERY,
} from 'modules/queryBuilder/legacyQueryBuilder/helpers';
import React from 'react';
import { WarningBanner } from 'ui/banner';
import { Skeleton } from 'ui/skeleton';
import noop from 'utils/noop';
import { QueryBuilderIndex } from '../types';
import {
  CombinatorContainer,
  QueryBuilderCSSContainer,
  QueryBuilderContainer,
  StyledCloneWarningsHeader,
  StyledClonedItems,
} from './QueryBuilder.styled';
import { ValidationError } from './ValidationError';
import CombinatorDropdown from './combinatorDropdown';
import LimitAndOrderByRow from './limitAndOrderByRow';
import LimitAndRecosOrOrderByRow from './limitAndRecosOrOrderByRow';
import SearchGroup from './searchGroup';
import SegmentConnector from './segmentConnector';
import { StateButtons } from './stateButtons';
import { ElementIndexContextProvider, ElementType } from '../contexts';

// types for this component (QueryBuilderV1Props) can be found in src/modules/queryBuilder/legacyQueryBuilder/types.ts
export default class QueryBuilder extends React.Component {
  static defaultProps = {
    combinatorAppendText: 'of the following groups',
    combinatorPrependText: 'Items in this collection belong to',
    emptySearchGroup: EMPTY_SEARCH_GROUP,
    emptySearchQuery: EMPTY_SEARCH_QUERY,
    emptyCriteriaProperty: EMPTY_CRITERIA_PROPERTY,
    fetchUserMappings: noop,
    filteredFieldsByPropertyMap: {},
    getFieldsForProperty: noop,
    hiddenPropertySections: [],
    hiddenProperties: [],
    disabledProperties: [],
    groupCombinatorAppendText: 'of the following clauses',
    groupCombinatorPrependText: 'Items satisfy',
    hideGroupsOnSingle: false,
    hideProperty: false,
    initializeQuery: noop,
    isProductRecommendationsOn: false,
    isUserContextLoaded: false,
    isReadOnly: false,
    isViewMode: false,
    loadingMessage: 'Loading...',
    onAddSearchGroup: noop,
    onChangeCombinator: noop,
    onSearchQueryMove: noop,
    onSearchQueryReorder: noop,
    orderBy: {},
    propertyPlaceholder: 'Choose a property',
    searchGroups: [],
    showFieldTextInput: false,
    showCustomDateTextInput: false,
    showDynamicFieldToggle: false,
    showStateButtons: true,
    showLimitAndOrderBy: false,
    useCastTypeField: false,
    validationMessageHeader: 'Your Query',
    esIndex: QueryBuilderIndex.UserAndEvent,
    canSetMaxPropertyCount: true,
  };

  componentDidMount = () => {
    const {
      comparatorDropdownProps,
      emptyCriteriaProperty,
      emptySearchGroup,
      emptySearchQuery,
      fetchUserMappings,
      hideProperty,
      initializeQuery,
      isReadOnly,
      isViewMode,
      orderBy,
      propertyPlaceholder,
      showCustomDateTextInput,
      showDynamicFieldToggle,
      sizeLimit,
      sizeLimitMax,
      type,
      esIndex,
      disallowHandlebarsInQuery,
    } = this.props;

    const queryBuilderProps = {
      comparatorDropdownProps,
      emptyCriteriaProperty,
      emptySearchQuery,
      hideProperty,
      isReadOnly,
      isViewMode,
      propertyPlaceholder,
      showDynamicFieldToggle,
      showCustomDateTextInput,
      sizeLimit,
      sizeLimitMax,
      type,
      esIndex,
      disallowHandlebarsInQuery,
      ...orderBy,
    };
    initializeQuery(emptySearchGroup, queryBuilderProps);

    if (showDynamicFieldToggle) {
      fetchUserMappings();
    }
  };

  render() {
    const {
      canManageCatalogs,
      children,
      combinator,
      combinatorAppendText,
      combinatorPrependText,
      hiddenPropertySections,
      hiddenProperties,
      disabledProperties,
      emptySearchGroup,
      filteredFieldsByPropertyMap,
      getFieldsForProperty,
      groupCombinatorAppendText,
      groupCombinatorPrependText,
      hideGroupsOnSingle,
      isLoading,
      isProductRecommendationsOn,
      isUserContextLoaded,
      isReadOnly,
      isViewMode,
      limitAndOrderByRowContent,
      onAddSearchGroup,
      onChangeCombinator,
      onSearchQueryMove,
      onSearchQueryReorder,
      resetPreviewIdentifier,
      searchGroups,
      showStateButtons,
      showLimitAndOrderBy,
      store,
      useCastTypeField,
      validationMessageHeader,
      cloningValidationMessages,
      hasCloningErrors,
      restrictToProperties,
      properties,
      showFieldTextInput,
      type,
    } = this.props;
    const numberOfGroups = searchGroups.length;
    const numberOfGroupsToShow = hideGroupsOnSingle ? 1 : 0;
    const multipleGroups = numberOfGroups > numberOfGroupsToShow;
    const handleAddSearchGroup = () => onAddSearchGroup(emptySearchGroup);
    const handleChangeCombinator = (e, { value }) => onChangeCombinator(value);

    return isLoading ? (
      <Skeleton height={200} />
    ) : (
      <QueryBuilderCSSContainer>
        {hasCloningErrors && (
          <WarningBanner>
            <StyledCloneWarningsHeader>
              Some copied fields could not be matched from the original project
            </StyledCloneWarningsHeader>
            <div>
              Please update the missing dependencies to refer to this project&apos;s data and
              resources
            </div>
          </WarningBanner>
        )}
        {cloningValidationMessages?.map(
          ({ validationMessageHeader: header, validationMessages: messageObjects }) => {
            return (
              <WarningBanner key={header}>
                <StyledCloneWarningsHeader>{header}</StyledCloneWarningsHeader>
                {messageObjects.map(({ location, items }) => (
                  <>
                    <div key={location}>{location}</div>
                    <StyledClonedItems>
                      {items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </StyledClonedItems>
                  </>
                ))}
              </WarningBanner>
            );
          },
        )}
        <ValidationError title={validationMessageHeader} />
        <QueryBuilderContainer
          data-test="query-builder"
          data-role="queryBuilder"
          data-view-mode={isViewMode || undefined}
          className="cssQueryBuilderContainer"
        >
          {children}
          {showStateButtons && (
            <StateButtons canManageCatalogs={canManageCatalogs} isViewMode={isViewMode} />
          )}
          {(multipleGroups || isViewMode) && (
            <CombinatorContainer>
              <SegmentConnector isMainConnector isTopConnector value={combinator} />
              {!isViewMode && numberOfGroups !== 1 && (
                <CombinatorDropdown
                  appendText={combinatorAppendText}
                  prependText={combinatorPrependText}
                  className="cssCombinatorDropdown"
                  onChange={handleChangeCombinator}
                  value={combinator}
                  isReadOnly={isReadOnly}
                />
              )}
              {isViewMode && <div className="cssCombinatorSpacer" />}
            </CombinatorContainer>
          )}
          <DragAndDropList onMove={onSearchQueryMove} onReorder={onSearchQueryReorder}>
            {searchGroups.map((query, index) => (
              <ElementIndexContextProvider
                index={index}
                type={ElementType.Criteria}
                key={`search-group-${index}}`}
              >
                <SearchGroup
                  type={type}
                  hiddenProperties={hiddenProperties}
                  hiddenPropertySections={hiddenPropertySections}
                  disabledProperties={disabledProperties}
                  getFieldsForProperty={getFieldsForProperty}
                  groupCombinator={combinator}
                  groupCombinatorAppendText={groupCombinatorAppendText}
                  groupCombinatorPrependText={groupCombinatorPrependText}
                  hideGroupsOnSingle={hideGroupsOnSingle}
                  onAddSearchGroup={handleAddSearchGroup}
                  searchGroupIndex={index}
                  showFieldTextInput={showFieldTextInput}
                  store={store}
                  totalGroups={searchGroups.length}
                  useCastTypeField={useCastTypeField}
                  isReadOnly={isReadOnly}
                  filteredFieldsByPropertyMap={filteredFieldsByPropertyMap}
                  restrictToProperties={restrictToProperties}
                  properties={properties}
                  canSetMaxPropertyCount={this.props.canSetMaxPropertyCount}
                />
              </ElementIndexContextProvider>
            ))}
          </DragAndDropList>
          {(isViewMode || multipleGroups) && (
            <Button table onClick={handleAddSearchGroup} disabled={isViewMode || isReadOnly}>
              Add a group
            </Button>
          )}
        </QueryBuilderContainer>
        {showLimitAndOrderBy &&
          isUserContextLoaded &&
          (isProductRecommendationsOn ? (
            <LimitAndRecosOrOrderByRow
              content={limitAndOrderByRowContent}
              store={store}
              onToggle={resetPreviewIdentifier}
            />
          ) : (
            <LimitAndOrderByRow content={limitAndOrderByRowContent} store={store} />
          ))}
      </QueryBuilderCSSContainer>
    );
  }
}
